import { useState } from 'react'
import { Inbox } from 'lucide-react'
import { approveSession, rejectSession } from '../lib/walletkit.js'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { Proposal } from '../hooks/useWalletKit.js'

interface Props {
  proposal: Proposal
  onDone: () => void
}

export function ProposalCard({ proposal, onDone }: Props) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const meta = proposal.params.proposer.metadata

  const approve = async () => {
    setBusy(true)
    setErr(null)
    try {
      await approveSession(proposal.id)
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    setBusy(true)
    try {
      await rejectSession(proposal.id)
      onDone()
    } finally {
      setBusy(false)
    }
  }

  const requiredChains =
    proposal.params.requiredNamespaces?.eip155?.chains ??
    proposal.params.optionalNamespaces?.eip155?.chains ??
    []

  return (
    <Card className="mb-4 border-l-4 border-l-amber-500">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Inbox className="size-4 text-amber-500" />
          Pairing request from {meta.name || 'unknown dApp'}
        </CardTitle>
        <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-400">
          awaiting approval
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">URL</dt>
          <dd className="break-all font-mono">{meta.url}</dd>
          <dt className="text-muted-foreground">Description</dt>
          <dd>{meta.description || '—'}</dd>
          <dt className="text-muted-foreground">Requested chains</dt>
          <dd className="font-mono">{requiredChains.join(', ') || '—'}</dd>
        </dl>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => void approve()} disabled={busy}>
            Approve
          </Button>
          <Button variant="outline" onClick={() => void reject()} disabled={busy}>
            Reject
          </Button>
          {err && <span className="text-sm text-destructive">{err}</span>}
        </div>
      </CardContent>
    </Card>
  )
}
